import { useEffect, useCallback } from 'react';

import { MAIN_BOOKER_FORM, FELLOW_TRAVELER_FORM } from '@/constants/form';
import { useItineraryContext } from '@/context/ItineraryContext';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useResource } from '@/hooks/useResource';

import type { CreateItineraryTraveler, ItineraryTravelerType } from '@/types/travelers';

export function useItineraryTraveler({
  onSaved,
  travelerType,
}: {
  onSaved: () => void;
  travelerType: ItineraryTravelerType;
}) {
  const { itineraryId } = useItineraryContext();

  const [localStorageData, setLocalStorageData] = useLocalStorage<Partial<CreateItineraryTraveler>>({
    key: `itinerary-traveler-${itineraryId}`,
    defaultValue: {},
  });

  const onSaveEmptyLocalStorage = useCallback(() => {
    onSaved();
    setLocalStorageData({});
  }, [onSaved, setLocalStorageData]);

  const data = useResource<Partial<CreateItineraryTraveler>, CreateItineraryTraveler>({
    endpoint: `/proxy/itinerary/${itineraryId}/itinerary-traveler`,
    initial: localStorageData,
    fields: travelerType === 'Main Booker' ? MAIN_BOOKER_FORM : FELLOW_TRAVELER_FORM,
    onSaved: onSaveEmptyLocalStorage,
    pause: !itineraryId,
  });

  // save current form data to local storage
  useEffect(() => {
    setLocalStorageData(data.formData);
  }, [data.formData, setLocalStorageData]);

  return {
    ...data,
    fields: MAIN_BOOKER_FORM,
  };
}
